import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { BoxProps } from '@material-ui/core';
import Image, { StaticImageData } from 'next/image';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import cateringPersonImage from '@images/food-program-page/services/catering-person.webp';
import notebookImage from '@images/food-program-page/services/notebook.webp';
import smilingPersonImage from '@images/food-program-page/services/smiling-person.webp';
import CheckIcon from '@images/homepage/redesign/check-icon.png';
import { marketplaceURL } from 'shared/helpers/urls';
import { useFoodProgramCta } from 'static_pages/foodProgram/useFoodProgramCta';
import { FoodProgramH2 } from 'static_pages/foodProgram/Utils';
import Container from 'system/components/Container';

const OurServices = (props: BoxProps) => {
  const { t } = useTranslation('food_program');
  const foodProgramCtaHref = useFoodProgramCta();

  const services = [
    {
      title: t('services.marketplace.title'),
      toWhom: t('services.marketplace.toWhom'),
      needs: t('services.marketplace.needs'),
      benefits: t('services.marketplace.benefits', {
        returnObjects: true,
      }) as string[],
      image: notebookImage,
      cta: {
        text: t('services.marketplace.ctaText'),
        label: null,
        href: marketplaceURL('/catering'),
        primary: false,
      },
    },
    {
      title: t('services.catering.title'),
      toWhom: t('services.catering.toWhom'),
      needs: t('services.catering.needs'),
      benefits: t('services.catering.benefits', {
        returnObjects: true,
      }) as string[],
      image: smilingPersonImage,
      cta: {
        text: t('services.catering.ctaText'),
        label: null,
        href: '/',
        primary: false,
      },
    },
    {
      title: t('services.foodProgram.title'),
      toWhom: t('services.foodProgram.toWhom'),
      needs: t('services.foodProgram.needs'),
      benefits: t('services.foodProgram.benefits', {
        returnObjects: true,
      }) as string[],
      image: cateringPersonImage,
      cta: {
        text: t('services.foodProgram.ctaText'),
        label: t('hero.ctaLabelBottom'),
        href: foodProgramCtaHref,
        primary: true,
      },
    },
  ].map((service) =>
    Array.isArray(service.benefits) ? service : { ...service, benefits: [] }
  );

  return (
    <Box as="section" {...props}>
      <Container size="sm">
        <FoodProgramH2 textAlign="center" mb={6}>
          {t('services.title')}
        </FoodProgramH2>
      </Container>
      <Container size="md">
        <Services>
          {services.map(({ title, cta, benefits, toWhom, needs, image }) => {
            return (
              <Service key={title}>
                <IdeaImage src={image} />
                <ServiceTextSection>
                  <Box alignSelf={'center'}>
                    <Typography
                      lineHeight={{ xs: 1.5, sm: 1.5 }}
                      fontSize={20}
                      alignSelf="center"
                      component="h3"
                      mb={2}
                      fontWeight={600}
                    >
                      {title}
                    </Typography>
                  </Box>
                  <Typography fontWeight={600}>
                    {t('services.labels.toWhom')}
                  </Typography>
                  <Typography>{toWhom}</Typography>
                  <Typography fontWeight={600}>
                    {t('services.labels.needs')}
                  </Typography>
                  <Typography>{needs}</Typography>
                  <Typography fontWeight={600}>
                    {t('services.labels.benefits')}
                  </Typography>
                  <BenefitItems>
                    {benefits.map((benefit) => (
                      <Box
                        key={benefit}
                        alignSelf="flex-start"
                        alignItems={'center'}
                        display="flex"
                      >
                        <Box flexShrink={0}>
                          <Image
                            alt=""
                            src={CheckIcon}
                            layout="fixed"
                            width={25}
                            height={19}
                          />
                        </Box>
                        <Typography fontSize={16} ml={2}>
                          {benefit}
                        </Typography>
                      </Box>
                    ))}
                  </BenefitItems>
                </ServiceTextSection>
                <Box mt={2}>
                  <StyledButton
                    rounded
                    target="_blank"
                    variant={cta.primary ? 'primary' : 'tertiary'}
                    component="a"
                    href={cta.href}
                  >
                    {cta.text}
                  </StyledButton>
                </Box>
                <Typography>
                  {cta.label || ' '.replace(/ /g, '\u00a0')}
                </Typography>
              </Service>
            );
          })}
        </Services>
      </Container>
    </Box>
  );
};

const StyledButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing(1.5, 4)};
  font-size: 16px;
`;

const Services = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  gap: ${({ theme }) => theme.spacing(3)}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    gap: ${({ theme }) => theme.spacing(4)}px;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    gap: ${({ theme }) => theme.spacing(9)}px;
  }
`;

const Service = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  height: revert;
  width: 305px;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)}px;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    max-width: 305px;
  }
`;

const ServiceTextSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(2)}px;
  flex-direction: column;
  flex-grow: 1;
  align-items: start;
  gap: ${({ theme }) => theme.spacing(2)}px;
  border-radius: 8px;
  background: #f8f8f8;
  margin-top: ${({ theme }) => theme.spacing(2)}px;
`;

const IdeaImage = (props: { src: string | StaticImageData }) => (
  <Image src={props.src} quality={100} objectFit="contain" alt="" />
);

const BenefitItems = styled.div`
  display: flex;
  align-self: start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)}px;
`;

export default OurServices;
